import { ManeuverOutcomeValue, TemCompetencyOutcomeDraft } from "src/app/models/models";
import { OutcomeSlot, outcomeCommentField } from "./ts1-rating-outcome-interaction";

/** Canonical write/read slot for TEM Rating C — stable across PF/PM role swap. */
export function temCompetencySlotForCandidateIndex(
	candidateIndex: number,
	hasSecondCandidate: boolean,
): OutcomeSlot {
	if (candidateIndex === 0) return "pfPrimary";
	return hasSecondCandidate ? "pmPrimary" : "pfPrimary";
}

/** Legacy role-based slots that may hold pre-fix data for this candidate index. */
export function legacyTemCompetencySlotsForCandidateIndex(candidateIndex: number): OutcomeSlot[] {
	if (candidateIndex === 0) return ["pm"];
	if (candidateIndex === 1) return ["pf"];
	return [];
}

function isStoredTemRating(value: ManeuverOutcomeValue | null | undefined): boolean {
	if (value == null) return false;
	if (value === "na" || value === "inc") return false;
	if (typeof value === "number") return value > 0;
	return false;
}

/** Read Rating C from canonical slot (call after normalize). */
export function readTemCompetencyRatingC(
	outcome: TemCompetencyOutcomeDraft | undefined,
	candidateIndex: number,
	hasSecondCandidate: boolean,
): ManeuverOutcomeValue | null | undefined {
	const slot = temCompetencySlotForCandidateIndex(candidateIndex, hasSecondCandidate);
	return outcome?.[slot];
}

function normalizeTemCompetencyOutcome(
	outcome: TemCompetencyOutcomeDraft,
	candidateCount: number,
	viewingCandidateIndex?: number,
): TemCompetencyOutcomeDraft {
	const hasSecondCandidate = candidateCount >= 2;
	let next = { ...outcome };
	const indices =
		viewingCandidateIndex !== undefined
			? [viewingCandidateIndex]
			: Array.from({ length: candidateCount }, (_, index) => index);

	for (const candidateIndex of indices) {
		const canonical = temCompetencySlotForCandidateIndex(candidateIndex, hasSecondCandidate);
		const canonicalValue = next[canonical];

		if (!isStoredTemRating(canonicalValue)) {
			for (const legacySlot of legacyTemCompetencySlotsForCandidateIndex(candidateIndex)) {
				const legacyValue = next[legacySlot];
				if (!isStoredTemRating(legacyValue)) continue;

				next = { ...next, [canonical]: legacyValue };
				const legacyCommentKey = outcomeCommentField(legacySlot);
				const canonicalCommentKey = outcomeCommentField(canonical);
				if (next[legacyCommentKey as keyof TemCompetencyOutcomeDraft] != null) {
					next = {
						...next,
						[canonicalCommentKey as keyof TemCompetencyOutcomeDraft]:
							next[legacyCommentKey as keyof TemCompetencyOutcomeDraft],
					};
				}
				break;
			}
		}

		for (const legacySlot of legacyTemCompetencySlotsForCandidateIndex(candidateIndex)) {
			if (legacySlot === canonical) continue;
			if (next[legacySlot] == null && next[outcomeCommentField(legacySlot) as keyof TemCompetencyOutcomeDraft] == null) {
				continue;
			}
			next = {
				...next,
				[legacySlot]: undefined,
				[outcomeCommentField(legacySlot) as keyof TemCompetencyOutcomeDraft]: undefined,
			};
		}
	}

	return next;
}

/**
 * Move legacy role-slot values into canonical candidate-index slots.
 * Pure — returns a new array.
 */
export function normalizeTemCompetencyOutcomes(
	outcomes: TemCompetencyOutcomeDraft[],
	candidateCount: number,
	viewingCandidateIndex?: number,
): TemCompetencyOutcomeDraft[] {
	if (!outcomes.length || candidateCount <= 0) return outcomes;
	return outcomes.map((outcome) =>
		normalizeTemCompetencyOutcome(outcome, candidateCount, viewingCandidateIndex),
	);
}

/** Clear legacy role slots for one candidate after writing Rating C to the canonical slot. */
export function clearLegacyTemCompetencySlotsForCandidate(
	outcome: TemCompetencyOutcomeDraft,
	candidateIndex: number,
): TemCompetencyOutcomeDraft {
	let next = { ...outcome };
	for (const legacySlot of legacyTemCompetencySlotsForCandidateIndex(candidateIndex)) {
		if (next[legacySlot] == null && next[outcomeCommentField(legacySlot) as keyof TemCompetencyOutcomeDraft] == null) {
			continue;
		}
		next = {
			...next,
			[legacySlot]: undefined,
			[outcomeCommentField(legacySlot) as keyof TemCompetencyOutcomeDraft]: undefined,
		};
	}
	return next;
}
